import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { IconWeight } from '../../models';
import { ToastService, ToastSeverity } from '../../services/toast.service';
import { GsIconComponent } from '../gs-icon/gs-icon.component';

interface IconConfig {
  /** Name of the icon */
  iconName: string;
  /** Weight of the icon */
  iconWeight: IconWeight;
}

@Component({
  selector: 'gs-toast',
  imports: [CommonModule, GsIconComponent, TranslatePipe],
  templateUrl: './gs-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host > div {
        z-index: 5000;
      }

      @keyframes gs-toast-enter {
        from {
          opacity: 0;
          transform: translateY(0.75rem) scale(0.98);
        }
      }

      @keyframes gs-toast-leave {
        to {
          opacity: 0;
          transform: translateY(0.75rem) scale(0.98);
        }
      }

      .gs-toast-enter {
        animation: gs-toast-enter 200ms ease-out;
      }

      .gs-toast-leave {
        animation: gs-toast-leave 150ms ease-in forwards;
      }

      @media (prefers-reduced-motion: reduce) {
        .gs-toast-enter,
        .gs-toast-leave {
          animation-duration: 1ms;
        }
      }
    `
  ]
})
export class GsToastComponent {
  // Services
  protected readonly toastService = inject(ToastService);

  // Properties & Signals
  /** Styles for different severity levels including border and background colors */
  readonly severityStyles: Record<ToastSeverity, string> = {
    success: 'border-success-shade bg-success-main text-success-contrast',
    info: 'border-primary-shade bg-primary-main text-primary-contrast',
    warn: 'border-warning-shade bg-warning-main text-warning-contrast',
    error: 'border-danger-shade bg-danger-main text-danger-contrast'
  };
  /** Text colors for different severity levels */
  readonly severityTextColors: Record<ToastSeverity, string> = {
    success: 'text-success-contrast',
    info: 'text-primary-contrast',
    warn: 'text-warning-contrast',
    error: 'text-danger-contrast'
  };
  /** Icons to display for each severity level */
  readonly severityIcons: Record<ToastSeverity, IconConfig> = {
    success: { iconName: 'check', iconWeight: 'light' },
    info: { iconName: 'info', iconWeight: 'light' },
    warn: { iconName: 'warning', iconWeight: 'light' },
    error: { iconName: 'warning', iconWeight: 'light' }
  };
  /** Classes anchoring the toast stack to the configured screen position */
  readonly positionClasses: Signal<string> = computed(() => {
    const position = this.toastService.position();
    const vertical = position.startsWith('top') ? 'top-4 flex-col' : 'bottom-4 flex-col-reverse';
    const horizontal = position.endsWith('left') ? 'items-start' : position.endsWith('right') ? 'items-end' : 'items-center';
    return `${vertical} ${horizontal}`;
  });

  // Methods
  /**
   * Reports how urgently a screen reader should announce a toast.
   * @param severity - Severity level of the toast.
   * @returns `alert` for errors and warnings, `status` otherwise.
   */
  getRole(severity: ToastSeverity): 'alert' | 'status' {
    return severity === 'error' || severity === 'warn' ? 'alert' : 'status';
  }
}
